import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Shift } from '../../shifts/services/ShiftService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import PlantafelService, { GanttShiftPosition, WorkArea, OperationsSettings } from '../services/PlantafelService';
import PlantafelGanttBar from './PlantafelGanttBar';
import {
  TIME_CONSTANTS,
  getWeekStartDateTime,
  getTodayDateStr,
  clampMinutes,
} from '../utils/plantafelTime';
import {
  getGridSettings,
  clientXToGridX,
  getDayIndexFromX,
  gridToDateTime,
  getWorkWindowMinutes,
  GridSettings,
} from '../utils/plantafelGridTime';
import { pdebug, pdebugWarn, pdebugError } from '../utils/plantafelDebug';

const { MINUTES_PER_DAY, MINUTES_PER_WEEK, SNAP_MINUTES, DEFAULT_DAY_START_MINUTES, DEFAULT_DAY_END_MINUTES } = TIME_CONSTANTS;

interface PlantafelGanttRowProps {
  workArea: WorkArea;
  parentWorkAreaId: number;
  shifts: Shift[];
  taskMap: Map<number, CrmTask>;
  weekStart: Date;
  weekStartStr: string;
  days: { date: Date; dateStr: string; label: string }[];
  colWidth: number;
  settings?: OperationsSettings;
  hourMarkers?: number[];
  includeRegularShifts?: boolean;
  viewMode?: 'week' | 'month' | 'year' | 'custom';
  onShiftClick: (shift: Shift, task?: CrmTask | null) => void;
  onMoveShift: (shiftId: number, newStartDateTime: string, newWorkAreaId?: number) => Promise<void>;
  onResizeShift: (shiftId: number, newEndDateTime: string) => Promise<void>;
  onRescheduleShift?: (shiftId: number, newTeamId: number, teamName?: string) => Promise<void>;
  onDropTask?: (taskId: number, dayIndex: number, teamId: number) => void;
  onAssignEmployee?: (shiftId: number, employeeId: number, employeeName?: string, fromShiftId?: number) => Promise<void>;
  onAssignAsset?: (shiftId: number, assetId: number) => Promise<void>;
  onUnscheduleShift?: (shiftId: number) => void;
  onDragStart?: (shiftId: number, workAreaId: number) => void;
  onDragEnd?: () => void;
  externalDragShiftId?: number | null;
  isDropTarget?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onCreateJob?: (teamId: number, dateStr: string) => void;
  highlightedShiftId?: number | null;
}

const BAR_HEIGHT = 90;
const FIXED_ROW_HEIGHT = 100;

/**
 * Gets the scroll offset from the nearest scrollable ancestor.
 */
function getScrollLeft(element: HTMLElement | null): number {
  if (!element) return 0;
  let parent = element.parentElement;
  while (parent) {
    if (parent.scrollWidth > parent.clientWidth) {
      return parent.scrollLeft;
    }
    parent = parent.parentElement;
  }
  return 0;
}

/**
 * Calculates X position to absolute week minutes, accounting for work window.
 * The grid only shows work hours (e.g., 06:00-20:00), so we need to convert
 * grid position to actual time of day.
 */
function clientXToAbsoluteMinutes(
  clientX: number,
  rowElement: HTMLElement,
  totalWidth: number,
  dayStartMinutes: number,
  dayEndMinutes: number
): number {
  const rect = rowElement.getBoundingClientRect();
  const scrollLeft = getScrollLeft(rowElement);
  const x = (clientX - rect.left) + scrollLeft;

  const colWidth = totalWidth / 7;
  const dayIndex = Math.floor(x / colWidth);
  const xWithinDay = x - (dayIndex * colWidth);

  const workWindowMinutes = dayEndMinutes - dayStartMinutes;
  const minutesWithinWindow = (xWithinDay / colWidth) * workWindowMinutes;

  // Convert to absolute week minutes: day offset + day start + position in window
  return dayIndex * MINUTES_PER_DAY + dayStartMinutes + minutesWithinWindow;
}

export default function PlantafelGanttRow({
  workArea,
  parentWorkAreaId,
  shifts,
  taskMap,
  weekStart,
  weekStartStr,
  days,
  colWidth,
  settings,
  hourMarkers = [],
  includeRegularShifts = false,
  viewMode = 'week',
  onShiftClick,
  onMoveShift,
  onResizeShift,
  onRescheduleShift,
  onDropTask,
  onAssignEmployee,
  onAssignAsset,
  onUnscheduleShift,
  onDragStart,
  onDragEnd,
  externalDragShiftId,
  isDropTarget = false,
  scrollContainerRef,
  onCreateJob,
  highlightedShiftId,
}: PlantafelGanttRowProps): React.JSX.Element {
  // IMPORTANT: Use same defaults as PlantafelService (6*60, 18*60)
  // to avoid position mismatches when settings are not loaded
  const dayStartMinutes = settings?.day_start_minutes ?? 6 * 60;  // 06:00 - matches service default
  const dayEndMinutes = settings?.day_end_minutes ?? 18 * 60;     // 18:00 - matches service default
  const workHoursPerDay = (dayEndMinutes - dayStartMinutes) / 60;
  const rowRef = useRef<HTMLDivElement>(null);

  // Click-after-drag suppression: track when last drag/resize ended
  const lastDragEndAtRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  const gridSettings: GridSettings = useMemo(() => getGridSettings(settings), [settings]);

  const [dragState, setDragState] = useState<{
    type: 'move' | 'resize';
    shiftId: number;
    startX: number;
    startDayIndex: number;
    currentDelta: number;
    currentEndX?: number;
    originalEndX?: number; // Original end X for delta-based resize
    currentLeft?: number;
    originalLeft?: number;
    originalStartMinutes?: number;
    originalEndMinutes?: number;
    originalStartIso?: string;
    originalEndIso?: string;
    grabOffset?: number; // Where within the bar the user grabbed (px from left edge)
  } | null>(null);

  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragOver, setIsDragOver] = useState(false);

  const totalWidth = days.length * colWidth;

  // Get week start date object for calculations
  const weekStartDt = useMemo(() => getWeekStartDateTime(weekStartStr), [weekStartStr]);

  // Use getShiftsForTeam: filter by work_area_team_id for job shifts
  const { positions, invalidJobShifts } = useMemo(
    () => PlantafelService.getShiftsForTeam(
      shifts,
      workArea.id,          // teamId
      parentWorkAreaId,     // parentWorkAreaId for regular shifts
      weekStart,
      taskMap,
      totalWidth,
      settings,
      includeRegularShifts,
      viewMode,
      days.length
    ),
    [shifts, workArea.id, parentWorkAreaId, weekStart, taskMap, totalWidth, settings, includeRegularShifts, viewMode, days.length]
  );

  // Log invalid job shifts (for debugging)
  useEffect(() => {
    if (invalidJobShifts.length > 0) {
      pdebugWarn(`PlantafelGanttRow: ${invalidJobShifts.length} invalid job shifts detected (missing work_area_team_id)`);
    }
  }, [invalidJobShifts]);

  const rowHeight = FIXED_ROW_HEIGHT;

  const handleMoveStart = useCallback((shiftId: number, startX: number, startDayIndex: number, grabOffset: number) => {
    const pos = positions.find(p => p.shift.id === shiftId);
    isDraggingRef.current = true;

    pdebug('Move start:', {
      shiftId,
      startX,
      grabOffset,
      originalLeft: pos?.left,
    });

    setDragState({
      type: 'move',
      shiftId,
      startX,
      startDayIndex,
      currentDelta: 0,
      currentLeft: pos?.left,
      originalLeft: pos?.left,
      originalStartMinutes: pos?.originalStartMinutes,
      originalEndMinutes: pos?.originalEndMinutes,
      grabOffset, // Store where user grabbed the bar
    });
    if (onDragStart) {
      onDragStart(shiftId, workArea.id);
    }
  }, [onDragStart, workArea.id, positions]);

  const handleCancelMove = useCallback(() => {
    // Cancel mouse-based drag (called when native drag starts)
    setDragState(null);
    isDraggingRef.current = false;
  }, []);

  const handleResizeStart = useCallback((shiftId: number, startX: number, endDayIndex: number) => {
    const pos = positions.find(p => p.shift.id === shiftId);
    const shift = pos?.shift;
    isDraggingRef.current = true;

    // Calculate the original end X position for delta-based resize
    const originalEndX = pos?.left !== undefined && pos?.width !== undefined ? pos.left + pos.width : undefined;

    setDragState({
      type: 'resize',
      shiftId,
      startX,
      startDayIndex: endDayIndex,
      currentDelta: 0,
      currentEndX: originalEndX,
      originalEndX: originalEndX, // Store original for delta calculation
      originalLeft: pos?.left,
      originalStartMinutes: pos?.originalStartMinutes,
      originalEndMinutes: pos?.originalEndMinutes,
      originalStartIso: shift?.start_time,
      originalEndIso: shift?.end_time,
    });
  }, [positions]);

  useEffect(() => {
    if (!dragState) return;

    // Use grid settings for consistent calculations
    const workWindowMinutes = getWorkWindowMinutes(gridSettings);
    const snapMinutes = gridSettings.snapMinutes;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragState.startX;

      setMousePos({ x: e.clientX, y: e.clientY });

      if (dragState.type === 'resize' && rowRef.current && dragState.originalEndX !== undefined) {
        // Use delta-based resize: apply mouse movement to original end position
        const deltaX = e.clientX - dragState.startX;
        const rawEndX = dragState.originalEndX + deltaX;

        // Snap the end position using ROUND (nearest grid line)
        // Use rawEndX - 1 for day index to handle day boundary correctly
        // (end at position 160 should be day 0, not day 1)
        const endDayIdx = getDayIndexFromX(Math.max(0, rawEndX - 1), colWidth);
        const rawXInDay = Math.max(0, rawEndX) - endDayIdx * colWidth;
        const rawMinInWindow = (rawXInDay / colWidth) * workWindowMinutes;
        // Use ROUND for end times - snap to nearest grid line
        // Add small bias toward exact hour boundaries (within 2 minutes)
        const nearestHour = Math.round(rawMinInWindow / 60) * 60;
        const distanceToHour = Math.abs(rawMinInWindow - nearestHour);
        const adjustedMinInWindow = distanceToHour <= 2 ? nearestHour : rawMinInWindow;
        const snappedMinInWindow = Math.round(adjustedMinInWindow / snapMinutes) * snapMinutes;
        const clampedMinInWindow = Math.max(snapMinutes, Math.min(workWindowMinutes, snappedMinInWindow));
        const snappedXInDay = (clampedMinInWindow / workWindowMinutes) * colWidth;
        const newEndX = endDayIdx * colWidth + snappedXInDay;

        setDragState(prev => prev ? {
          ...prev,
          currentDelta: deltaX,
          currentEndX: newEndX,
        } : null);
      } else if (dragState.type === 'move' && rowRef.current && dragState.grabOffset !== undefined) {
        // For move: calculate the bar's LEFT EDGE position using grabOffset
        // This is the key fix: we track where the user grabbed the bar
        const rect = rowRef.current.getBoundingClientRect();
        const scrollLeft = getScrollLeft(rowRef.current);

        // Calculate where the bar's left edge should be
        // = mouse position - container offset + scroll - where user grabbed within bar
        const rawTargetLeft = e.clientX - rect.left + scrollLeft - dragState.grabOffset;

        // Get the day and position within day
        const targetDayIdx = getDayIndexFromX(Math.max(0, rawTargetLeft), colWidth);
        const rawXInDay = Math.max(0, rawTargetLeft) - targetDayIdx * colWidth;

        // Calculate minutes in window and snap to grid using ROUND (nearest grid line)
        // Add small bias toward exact hour boundaries (within 2 minutes)
        const rawMinInWindow = (rawXInDay / colWidth) * workWindowMinutes;
        const nearestHour = Math.round(rawMinInWindow / 60) * 60;
        const distanceToHour = Math.abs(rawMinInWindow - nearestHour);
        const adjustedMinInWindow = distanceToHour <= 2 ? nearestHour : rawMinInWindow;
        const snappedMinInWindow = Math.max(0, Math.min(workWindowMinutes,
          Math.round(adjustedMinInWindow / snapMinutes) * snapMinutes // ROUND to nearest
        ));

        // Convert back to pixel position (this is now on a true grid line)
        const snappedXInDay = (snappedMinInWindow / workWindowMinutes) * colWidth;
        const snappedLeft = Math.max(0, Math.min(totalWidth - 10, targetDayIdx * colWidth + snappedXInDay));

        setDragState(prev => prev ? {
          ...prev,
          currentDelta: 0, // Not needed anymore
          currentLeft: snappedLeft,
        } : null);
      }
    };

    const handleMouseUp = async () => {
      try {
        if (dragState.type === 'move') {
          // Calculate absolute target time from visual position (already snapped with FLOOR)
          if (dragState.currentLeft !== undefined &&
              dragState.originalLeft !== undefined &&
              dragState.currentLeft !== dragState.originalLeft) {

            // Get day index and position within day from the snapped pixel position
            const newDayIdx = getDayIndexFromX(dragState.currentLeft, colWidth);
            const newXInDay = dragState.currentLeft - newDayIdx * colWidth;

            // Calculate minutes directly from pixel position (already snapped in handleMouseMove)
            // Use ROUND for consistency with handleMouseMove
            // Add small bias toward exact hour boundaries (within 2 minutes)
            const rawMinInWindow = (newXInDay / colWidth) * workWindowMinutes;
            const nearestHour = Math.round(rawMinInWindow / 60) * 60;
            const distanceToHour = Math.abs(rawMinInWindow - nearestHour);
            const adjustedMinInWindow = distanceToHour <= 2 ? nearestHour : rawMinInWindow;
            const newMinInWindow = Math.round(adjustedMinInWindow / snapMinutes) * snapMinutes;

            // Convert to absolute datetime string using gridToDateTime
            const newStartDateTime = gridToDateTime(newDayIdx, newMinInWindow, weekStartDt, gridSettings);

            // Debug info
            const newTimeOfDay = gridSettings.dayStartMinutes + newMinInWindow;
            pdebug('Move commit (absolute time):', {
              originalLeft: dragState.originalLeft,
              currentLeft: dragState.currentLeft,
              newDayIdx,
              newMinInWindow,
              newTimeOfDay: `${Math.floor(newTimeOfDay / 60)}:${String(Math.round(newTimeOfDay % 60)).padStart(2, '0')}`,
              newStartDateTime,
            });

            await onMoveShift(dragState.shiftId, newStartDateTime);
          }
        } else if (dragState.type === 'resize' && dragState.currentEndX !== undefined) {
          const originalStartIso = dragState.originalStartIso;
          if (originalStartIso) {
            // Calculate end time
            // Use currentEndX - 1 for day index to handle day boundary correctly
            const endDayIdx = getDayIndexFromX(Math.max(0, dragState.currentEndX - 1), colWidth);
            const endXInDay = dragState.currentEndX - endDayIdx * colWidth;
            const rawEndMinInWindow = (endXInDay / colWidth) * workWindowMinutes;

            // Use ROUND for end times - snap to nearest grid line
            // Add small bias toward exact hour boundaries (within 2 minutes)
            const nearestHour = Math.round(rawEndMinInWindow / 60) * 60;
            const distanceToHour = Math.abs(rawEndMinInWindow - nearestHour);
            const adjustedEndMinInWindow = distanceToHour <= 2 ? nearestHour : rawEndMinInWindow;
            const snappedEndMinInWindow = Math.round(adjustedEndMinInWindow / snapMinutes) * snapMinutes;
            const clampedEndMinInWindow = Math.max(snapMinutes, Math.min(workWindowMinutes, snappedEndMinInWindow));

            const newEndDateTime = gridToDateTime(endDayIdx, clampedEndMinInWindow, weekStartDt, gridSettings);

            // Validate minimum duration
            const startDate = new Date(originalStartIso);
            const endDate = new Date(newEndDateTime);
            const durationMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);

            if (durationMinutes >= snapMinutes && newEndDateTime !== dragState.originalEndIso) {
              await onResizeShift(dragState.shiftId, newEndDateTime);
            }
          }
        }
      } catch (err) {
        pdebugError('Failed to update shift:', err);
      }
      // Mark drag as ended and record timestamp for click suppression
      isDraggingRef.current = false;
      lastDragEndAtRef.current = Date.now();
      setDragState(null);
      if (onDragEnd) {
        onDragEnd();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, totalWidth, weekStartDt, colWidth, gridSettings, onMoveShift, onResizeShift, onDragEnd]);

  const getAdjustedPosition = (pos: GanttShiftPosition): GanttShiftPosition => {
    if (!dragState || dragState.shiftId !== pos.shift.id) {
      return pos;
    }

    if (dragState.type === 'move' && dragState.currentLeft !== undefined) {
      // Use the tracked currentLeft directly (already snapped and clamped)
      const newLeft = dragState.currentLeft;

      // Calculate day indices from pixel positions
      const newStartDayIndex = getDayIndexFromX(newLeft, colWidth);
      const newEndDayIndex = getDayIndexFromX(newLeft + pos.width - 1, colWidth);

      return {
        ...pos,
        startDayIndex: newStartDayIndex,
        endDayIndex: Math.min(6, newEndDayIndex),
        left: newLeft,
        clampedLeft: newLeft <= 0,
        clampedRight: newLeft + pos.width >= totalWidth,
      };
    } else if (dragState.type === 'resize' && dragState.currentEndX !== undefined) {
      const newEndX = Math.max(pos.left + colWidth * 0.1, dragState.currentEndX);
      const newWidth = newEndX - pos.left;
      const endDayIndex = getDayIndexFromX(newEndX - 1, colWidth);

      return {
        ...pos,
        endDayIndex: Math.min(6, endDayIndex),
        width: newWidth,
        clampedRight: newEndX > totalWidth,
      };
    }

    return pos;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const taskId = e.dataTransfer.getData('taskId');
    // Read team ID directly from the drop target element (bulletproof)
    const targetTeamId = e.currentTarget.getAttribute('data-team-id');
    const teamIdFromElement = targetTeamId ? parseInt(targetTeamId, 10) : null;

    // DEBUG: Always log to console for troubleshooting
    console.log('[DROP DEBUG] handleDrop triggered:', {
      taskId,
      teamIdFromElement,
      workAreaId: workArea.id,
      workAreaName: workArea.name,
      hasOnDropTask: !!onDropTask,
      hasRowRef: !!rowRef.current,
      allDataTypes: e.dataTransfer.types,
    });

    pdebug('PlantafelGanttRow: handleDrop:', {
      taskId,
      teamIdFromElement,
      workAreaId: workArea.id,
      workAreaName: workArea.name,
      match: teamIdFromElement === workArea.id,
    });

    if (taskId && onDropTask && rowRef.current && teamIdFromElement) {
      const rect = rowRef.current.getBoundingClientRect();
      const scrollLeft = getScrollLeft(rowRef.current);
      const gridX = clientXToGridX(e.clientX, rect, scrollLeft);
      const dayIndex = getDayIndexFromX(gridX, colWidth);

      console.log('[DROP DEBUG] Calling onDropTask:', { taskId, dayIndex, teamId: teamIdFromElement });
      pdebug('PlantafelGanttRow: Calling onDropTask with:', { taskId, dayIndex, teamId: teamIdFromElement });
      onDropTask(parseInt(taskId, 10), dayIndex, teamIdFromElement);
    } else {
      console.warn('[DROP DEBUG] Drop NOT processed - missing:', {
        taskId: !taskId ? 'MISSING' : 'ok',
        onDropTask: !onDropTask ? 'MISSING' : 'ok',
        rowRef: !rowRef.current ? 'MISSING' : 'ok',
        teamIdFromElement: !teamIdFromElement ? 'MISSING' : 'ok',
      });
    }
  };

  const handleShiftDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('shiftId');
    const sourceWorkAreaId = e.dataTransfer.getData('workAreaId');
    const isJobShift = e.dataTransfer.getData('isJobShift') === 'true';

    if (shiftId && sourceWorkAreaId && rowRef.current) {
      const shiftIdNum = parseInt(shiftId, 10);
      const sourceWaId = parseInt(sourceWorkAreaId, 10);

      if (sourceWaId !== workArea.id) {
        pdebug('Cross-lane shift drop:', { shiftIdNum, sourceWaId, targetTeamId: workArea.id, isJobShift });

        // For job shifts, use rescheduleShift (dedicated endpoint)
        if (isJobShift && onRescheduleShift) {
          onRescheduleShift(shiftIdNum, workArea.id, workArea.name);
        } else {
          // For regular shifts, calculate new position and use moveShift
          const rect = rowRef.current.getBoundingClientRect();
          const scrollLeft = getScrollLeft(rowRef.current);
          const gridX = clientXToGridX(e.clientX, rect, scrollLeft);

          // Get day index and position within day from the drop position
          const dayIdx = getDayIndexFromX(gridX, colWidth);
          const xInDay = gridX - dayIdx * colWidth;

          // Calculate minutes in window using ROUND (nearest grid line)
          // Add small bias toward exact hour boundaries (within 2 minutes)
          const workWindowMinutes = getWorkWindowMinutes(gridSettings);
          const rawMinInWindow = (xInDay / colWidth) * workWindowMinutes;
          const nearestHour = Math.round(rawMinInWindow / 60) * 60;
          const distanceToHour = Math.abs(rawMinInWindow - nearestHour);
          const adjustedMinInWindow = distanceToHour <= 2 ? nearestHour : rawMinInWindow;
          const minInWindow = Math.round(adjustedMinInWindow / gridSettings.snapMinutes) * gridSettings.snapMinutes;

          // Convert to absolute datetime string using gridToDateTime
          const newStartDateTime = gridToDateTime(dayIdx, minInWindow, weekStartDt, gridSettings);

          onMoveShift(shiftIdNum, newStartDateTime, workArea.id);
        }
      }
    }
  }, [weekStartDt, workArea.id, workArea.name, onMoveShift, onRescheduleShift, colWidth, gridSettings]);

  const handleLaneClick = useCallback((e: React.MouseEvent) => {
    // Suppress click if we just finished dragging/resizing (within 300ms)
    if (isDraggingRef.current || Date.now() - lastDragEndAtRef.current < 300) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const isOnShift = (e.target as HTMLElement).closest('[data-role="shift-bar"]');
    if (isOnShift) return;

    if (!onCreateJob || !rowRef.current) return;

    const rect = rowRef.current.getBoundingClientRect();
    const scrollLeft = getScrollLeft(rowRef.current);
    const gridX = clientXToGridX(e.clientX, rect, scrollLeft);
    const dayIdx = getDayIndexFromX(gridX, colWidth);
    const dateStr = days[dayIdx]?.dateStr;

    if (dateStr) {
      onCreateJob(workArea.id, dateStr);
    }
  }, [onCreateJob, colWidth, days, workArea.id]);

  return (
    <div
      ref={rowRef}
      data-testid={`plantafel-row-${workArea.id}`}
      data-team-id={workArea.id}
      onClick={handleLaneClick}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={(e) => {
        handleDrop(e);
        handleShiftDrop(e);
      }}
      style={{
        position: 'relative',
        height: rowHeight,
        borderBottom: '1px solid #e0e3ef',
        background: isDragOver ? '#c7f5d9' : isDropTarget ? '#e0e7ff' : dragState ? '#f8f9ff' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      {days.map((day, dayIdx) => {
        const isEvenDay = dayIdx % 2 === 0; // Mo=0, Di=1, Mi=2, ...
        const workWindowMinutes = gridSettings.dayEndMinutes - gridSettings.dayStartMinutes;
        const startHour = Math.floor(gridSettings.dayStartMinutes / 60);
        // Alternating backgrounds for better day distinction
        const dayBackground = isEvenDay ? 'rgba(0, 0, 0, 0.02)' : 'transparent';
        return (
          <div
            key={day.dateStr}
            style={{
              position: 'absolute',
              left: dayIdx * colWidth,
              top: 0,
              bottom: 0,
              width: colWidth,
              borderRight: '1px solid #e0e3ef',
              background: dayBackground,
              display: 'flex',
            }}
          >
            {/* Grid lines for hours - only in week view */}
            {viewMode === 'week' && hourMarkers.map((hour) => {
              if (hour === startHour) return null; // Skip first line (at column edge)
              const isEvenHour = hour % 2 === 0;
              // Calculate position based on minutes from day start, matching bar positioning
              const hourMinutes = hour * 60;
              const minutesFromStart = hourMinutes - gridSettings.dayStartMinutes;
              const linePosition = (minutesFromStart / workWindowMinutes) * 100;
              // Don't render if outside work window
              if (linePosition < 0 || linePosition > 100) return null;
              return (
                <div
                  key={hour}
                  style={{
                    position: 'absolute',
                    left: `${linePosition}%`,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    background: isEvenHour ? '#d0d3df' : '#e8eaef',
                    transform: 'translateX(-50%)', // Match header centering
                  }}
                />
              );
            })}
          </div>
        );
      })}

      {positions.map((pos) => {
        const adjustedPos = getAdjustedPosition(pos);
        const isDragging = dragState?.shiftId === pos.shift.id && dragState.type === 'move';
        const isResizing = dragState?.shiftId === pos.shift.id && dragState.type === 'resize';

        return (
          <PlantafelGanttBar
            key={pos.shift.id}
            position={adjustedPos}
            barHeight={BAR_HEIGHT}
            workAreaId={workArea.id}
            workAreaColor={workArea.color}
            onShiftClick={onShiftClick}
            onMoveStart={handleMoveStart}
            onResizeStart={handleResizeStart}
            onAssignEmployee={onAssignEmployee}
            onAssignAsset={onAssignAsset}
            onUnscheduleShift={onUnscheduleShift}
            onCancelMove={handleCancelMove}
            isDragging={isDragging}
            isResizing={isResizing}
            isHighlighted={highlightedShiftId === pos.shift.id}
          />
        );
      })}

    </div>
  );
}
